import { TreeItemProps } from '@mui/x-tree-view';
import { RichTreeView } from '@mui/x-tree-view/RichTreeView';
import React, { forwardRef, useMemo } from 'react';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { Task } from 'shared/types/task';

import { CustomTreeItem } from './custom-tree-item';
import { TreeNode } from './types';

export type CollapsableHierarchyListProps = {
  treeNodes: TreeNode[];
  tasks: Task[];
  allGoals: Goal[];
  allLifeAreas: LifeArea[];
  openedSections?: ID[];
  onCollapse?: (id: ID) => void;
  onExpandedItemsChange?: (ids: ID[]) => void;
  hasDnd?: boolean;
  preventDnd?: boolean;
  hideTaskDurationInteraction?: boolean;
};

export const CollapsableHierarchyList = forwardRef<
  HTMLUListElement,
  CollapsableHierarchyListProps
>(
  (
    {
      treeNodes,
      tasks,
      allLifeAreas,
      allGoals,
      openedSections,
      onCollapse,
      onExpandedItemsChange,
      hasDnd,
      preventDnd,
      hideTaskDurationInteraction,
    },
    ref,
  ) => {
    const idDataMap = useMemo(() => {
      const idTasksMap = tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {});
      const idGoalsMap = allGoals.reduce<Record<ID, Goal>>((acc, goal) => {
        acc[goal.id] = goal;
        return acc;
      }, {});
      const idLifeAreaMap = allLifeAreas.reduce<Record<ID, LifeArea>>(
        (acc, lifeArea) => {
          acc[lifeArea.id] = lifeArea;
          return acc;
        },
        {},
      );

      return {
        ...idTasksMap,
        ...idGoalsMap,
        ...idLifeAreaMap,
      };
    }, [allGoals, allLifeAreas, tasks]);

    const slots = useMemo(
      () => ({
        item: (props: TreeItemProps) => (
          <CustomTreeItem
            {...props}
            idDataMap={idDataMap}
            lifeAreas={allLifeAreas}
            goals={allGoals}
            hasDnd={hasDnd}
            preventDnd={preventDnd}
            onCollapse={onCollapse}
            hideTaskDurationInteraction={hideTaskDurationInteraction}
          />
        ),
      }),
      [
        allGoals,
        allLifeAreas,
        hasDnd,
        hideTaskDurationInteraction,
        idDataMap,
        onCollapse,
        preventDnd,
      ],
    );

    const _onExpandedItemsChange = onExpandedItemsChange
      ? (_: React.SyntheticEvent | null, ids: ID[]) =>
          onExpandedItemsChange(ids)
      : undefined;

    return (
      <RichTreeView
        ref={ref}
        items={treeNodes}
        slots={slots}
        expandedItems={openedSections}
        onExpandedItemsChange={_onExpandedItemsChange}
      />
    );
  },
);
